
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { HelpCircle } from 'lucide-react';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface HeaderSummaryProps {
  nevScore: number;
  rating: 'High Risk' | 'Competitive' | 'Exceptional';
  industryPercentile?: number;
  keyInsight?: string;
}

const HeaderSummary: React.FC<HeaderSummaryProps> = ({ nevScore, rating, industryPercentile, keyInsight }) => {
  const { t } = useLanguage();
  
  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'High Risk':
        return 'text-red-600';
      case 'Competitive':
        return 'text-amber-600';
      case 'Exceptional':
        return 'text-green-600';
      default:
        return 'text-primary';
    }
  };

  const getRatingEmoji = (rating: string) => {
    switch (rating) {
      case 'High Risk':
        return '🔴';
      case 'Competitive':
        return '🟡';
      case 'Exceptional':
        return '🟢';
      default:
        return '';
    }
  };

  // Convert rating to a format usable for translation keys
  const getRatingKey = (rating: string) => {
    return rating.toLowerCase().replace(' ', '');
  };

  return (
    <Card className="w-full bg-white border border-gray-200 shadow-sm">
      <CardContent className="pt-6">
        <div className="flex flex-wrap gap-8 justify-between items-center">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-4xl font-bold text-blue-600">{nevScore}/50</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={`text-xl font-medium ${getRatingColor(rating)} cursor-help`}>
                      {getRatingEmoji(rating)} {t(`risk.${getRatingKey(rating)}`)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm max-w-xs">
                      This rating represents your project's overall risk assessment based on emotional connection, 
                      longevity and leadership metrics.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {industryPercentile && (
              <p className="text-sm font-medium text-gray-600">
                Top {industryPercentile}% in industry
              </p>
            )}
          </div>
          
          {keyInsight && (
            <div className="flex-1 min-w-[200px]">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-1">Key Insight</h3>
              <p className="text-base text-gray-700">{keyInsight}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HeaderSummary;
